import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, LogOut, ChevronRight, X, Calendar, CheckCircle2, Clock, XCircle, Info, Building, Loader2 } from 'lucide-react';
import styles from './Header2.module.css';
import Avatar from '../../Assets/avat.svg'; 
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import { IoMdNotifications, IoMdNotificationsOutline } from 'react-icons/io';
import axios from 'axios';
import { BASE_URL } from '../Api/Api';
import Swal from 'sweetalert2';

export default function Header2({ title = 'Cooperative Member Portal' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const bellButtonRef = useRef(null);
  const profileRef = useRef(null); // Ref for outside clicks on profile dropdown

  const showBreadcrumbs = location.pathname !== '/dashboard' && location.pathname !== '/';
  
  // Local Storage Items
  const memberName = localStorage.getItem("memberName");
  const memberPhoto = localStorage.getItem("memberPhoto");
  const currentCompanyName = localStorage.getItem("companyName") || "No Company Selected";
  const userEmail = localStorage.getItem("memberEmail") || ""; 

  // UI States
  const [isOpen, setIsOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false); // Full-screen loader state

  // Notifications State
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Company Switcher States
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [emailError, setEmailError] = useState('');

  // 1. Fetch Companies API
 // 1. Updated Fetch Companies API to accommodate nested company/member structure
const fetchCompanies = async (userEmail) => {
  if (!userEmail) {
    setEmailError("No user email found to retrieve associated companies.");
    return false;
  }
  setCompaniesLoading(true);
  try {
    const compid = localStorage.getItem("companyId");
    const memberid = localStorage.getItem("memberId");
    const response = await axios.get(`${BASE_URL}/my-active-coop?email=${userEmail}&company_id=${compid}&member_id=${memberid}`);
    // Extracting the list directly from response
    const companyList = response.data?.companies || response.data?.data || [];
    
    setCompanies(companyList);

    if (companyList.length > 0) {
      setEmailError('');
      // Set default selected target using the nested company ID structure
      setSelectedCompanyId(companyList[0]?.company?.id || '');
      return true; 
    } else {
      setSelectedCompanyId('');
      setEmailError(response.data?.message || 'No company found.');
      return false;
    }
  } catch (error) {
    let errorMessage = 'An error occurred fetching companies. Please try again.';
    if (error.response && error.response.data && error.response.data.message) {
      const { message } = error.response.data;
      if (typeof message === 'string') errorMessage = message;
      else if (Array.isArray(message)) errorMessage = message.join('; ');
      else if (typeof message === 'object') {
        const firstKey = Object.keys(message)[0];
        const messages = message[firstKey];
        errorMessage = Array.isArray(messages) ? messages.join('; ') : messages;
      }
    }
    setEmailError(errorMessage);
    setCompanies([]);
    setSelectedCompanyId('');
    return false;
  } finally {
    setCompaniesLoading(false);
  }
};

// 2. Updated Switch Confirmation to dynamically stamp nested company & member data
const handleConfirmCompanySwitch = async () => {
  // Find match based on the ID inside the nested company object
  const selectedWorkspace = companies.find(
    (c) => String(c?.company?.id) === String(selectedCompanyId)
  );

  if (!selectedWorkspace) {
    Swal.fire("Error", "Please select a valid company.", "error");
    return;
  }

  const selectedCompanyObj = selectedWorkspace.company || {};
  const selectedMemberObj = selectedWorkspace.member || {};
  const newCompanyName = selectedCompanyObj.name || "";

  // Close company modal and begin switching animation
  setShowCompanyModal(false);
  setIsSwitching(true);

  // Promise wrapper for a strict 5-second delay
  await new Promise((resolve) => setTimeout(resolve, 5000));

  // Stamp Company Details
  localStorage.setItem("companyId", selectedCompanyObj.id || "");
  localStorage.setItem("companyName", newCompanyName);

  // Stamp Member Details based on selected new workspace
  localStorage.setItem("memberNumber", selectedMemberObj.member_number || "");
  localStorage.setItem("memberId", selectedMemberObj.id || "");
  localStorage.setItem("memberPhoneNumber", selectedMemberObj.phone_number || "");
  localStorage.setItem("department", selectedMemberObj.department || "");
  localStorage.setItem("employerName", selectedMemberObj.employer_name || "");
  localStorage.setItem("saveType", selectedMemberObj.savings_type || "");
  localStorage.setItem("memberName", selectedMemberObj.name || "");
  localStorage.setItem("memberAddress", selectedMemberObj.address || "");
  localStorage.setItem("memberPhoto", selectedMemberObj.passport_photograph || "");
  localStorage.setItem("walletBalance", selectedMemberObj.wallet_balance || "");
  localStorage.setItem("memberEmail", selectedMemberObj.email || "");
  
  // Turn off full-screen loader
  setIsSwitching(false);

  // Display success modal and redirect directly to dashboard
  Swal.fire({
    title: "Success!",
    text: `Successfully switched to ${newCompanyName}`,
    icon: "success",
    confirmButtonText: "Proceed to Dashboard",
    confirmButtonColor: "#16a34a",
  }).then(() => {
    // Navigate straight to the dashboard workspace
    navigate("/dashboard");
  });
};

  // 2. Handle Switch Company Trigger flow
  const handleSwitchCompanyClick = () => {
    setProfileDropdownOpen(false); // Close the little dropdown first

    Swal.fire({
      title: "Switch Company",
      text: "Are you sure you want to switch to another company you are associated with?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Switch",
      cancelButtonText: "No, Cancel",
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#e72f38",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        // Fetch fresh company list and show the selection modal
        fetchCompanies(userEmail);
        setShowCompanyModal(true);
      }
    });
  };

  // 3. Save Selected Company with 5s Delay and Dashboard Redirect


  // Notification Handler
  const handleMarkAsRead = async (notificationId, shouldNavigate = false) => {
    if (shouldNavigate) {
      setIsOpen(false);
      navigate("/loans/guarantor-approval");
    }

    try {
      const token = localStorage.getItem("token");
    const compid = localStorage.getItem("companyId");
    const memberid = localStorage.getItem("memberId");
      await axios.post(
        `${BASE_URL}/member-auth/mark_notification`,
        { notification_id: notificationId,
          company_id: compid,
          member_id: memberid
         },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      fetchNotifications();
    } catch (err) {
      console.error(err);
    }
  };

  const unreadNotifications = notifications.filter(n => !n.is_read);

  const handleNotificationClick = () => {
    if (!isOpen) {
      setIsOpen(true);
      fetchNotifications();
    } else {
      setIsOpen(false);
    }
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Logout",
      text: "Are you sure you want to logout of this system?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Logout",
      cancelButtonText: "No, Stay Logged In",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/login", { replace: true });
      }
    });
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const compid = localStorage.getItem("companyId");
    const memberid = localStorage.getItem("memberId");
      const response = await axios.get(
        `${BASE_URL}/member-auth/get_notifications?email=${userEmail}&company_id=${compid}&member_id=${memberid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      setNotifications(response.data.data || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Unable to fetch profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Handle outside clicks for notification panel and profile dropdown safely
  useEffect(() => {
    function handleClickOutside(event) {
      // Notification Panel Closer
      if (
        panelRef.current && 
        !panelRef.current.contains(event.target) &&
        bellButtonRef.current &&
        !bellButtonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
      
      // Profile Dropdown Closer
      if (
        profileRef.current && 
        !profileRef.current.contains(event.target)
      ) {
        setProfileDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <CheckCircle2 className={styles.statusSuccess} size={16} />;
      case 'pending': return <Clock className={styles.statusWarning} size={16} />;
      case 'rejected': return <XCircle className={styles.statusDanger} size={16} />;
      default: return <Info className={styles.statusInfo} size={16} />;
    }
  };

  const formatMemberName = (name) => {
    if (!name) return "";
    const titleCased = name
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    if (titleCased.length > 20) {
      return `${titleCased.slice(0, 20)}...`;
    }
    return titleCased;
  };

  const photo =
    memberPhoto &&
    memberPhoto !== "null" &&
    memberPhoto !== "undefined"
      ? memberPhoto
      : Avatar;

  return (
    <header className={styles.header}>
      <div className={styles.headerTitleBlock}>
        <h2>{title}</h2>
        {showBreadcrumbs && <Breadcrumb />}
      </div>
      
      <div className={styles.headerRight}>
        {/* NOTIFICATION TRIGGER BELL BUTTON */}
        <button 
          ref={bellButtonRef}
          className={`${styles.notifBell} ${isOpen ? styles.bellActive : ''}`} 
          onClick={handleNotificationClick}
          aria-label="Notifications"
        >
          {isOpen ? <IoMdNotifications size={20} /> : <IoMdNotificationsOutline size={20} />}
          {unreadNotifications.length > 0 && (
            <span className={styles.bellBadge}>{unreadNotifications.length}</span>
          )}
        </button>
        
        {/* AVATAR & DROPDOWN CONTAINER */}
        <div className={styles.profileContainer} ref={profileRef}>
          <div 
            className={styles.profileBox} 
            onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
          >
            <div className={styles.avatar}>
              <img 
                src={photo} 
                alt={memberName || "Member"} 
                onError={(e) => { e.target.src = Avatar; }}
              />
            </div>
            <h3 className={styles.profileName}>  
              {formatMemberName(memberName)}
            </h3>
            <ChevronRight 
              size={14} 
              style={{ 
                transform: profileDropdownOpen ? 'rotate(-90deg)' : 'rotate(90deg)', 
                color: '#94a3b8',
                transition: 'transform 0.2s ease'
              }} 
            />
          </div>

          {/* AVATAR DROPDOWN */}
          {profileDropdownOpen && (
            <div className={styles.avatarDropdown}>
              <button 
                type="button" 
                className={styles.dropdownItem} 
                onClick={handleSwitchCompanyClick}
              >
                <Building size={16} />
                <span>Switch Company</span>
              </button>
            </div>
          )}
        </div>

        <button className={styles.headerLogout} aria-label="Log out" onClick={handleLogout}>
          <LogOut size={16} />
          <span>Logout</span>
        </button>
      </div>

      {/* NOTIFICATION PANEL DRAWER */}
      <div 
        ref={panelRef} 
        className={`${styles.notificationPanel} ${isOpen ? styles.panelOpen : ''}`}
      >
        <div className={styles.panelHeader}>
          <div className={styles.panelHeaderTitle}>
            <h3>Notifications</h3>
            {unreadNotifications.length > 0 && (
              <span className={styles.countTag}>{unreadNotifications.length} New</span>
            )}
          </div>
          <button className={styles.closePanelBtn} onClick={() => setIsOpen(false)} aria-label="Close panel">
            <X size={18} />
          </button>
        </div>

        <div className={styles.panelBody}>
          {loading ? (
            <div className={styles.notificationLoader}>
              <div className={styles.loader}></div>
              <p>Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className={styles.emptyState}>
              <Bell size={40} strokeWidth={1.5} />
              <p>All Done!</p>
              <span>No new activity logs found on your account.</span>
            </div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.id}
                className={`${styles.notifCard} ${notif.is_read ? styles.readNotification : styles.unreadNotification}`}
              >
                <div className={styles.notifMeta}>
                  <div className={styles.iconWrapper}>{getStatusIcon(notif.approval_status)}</div>
                  <div className={styles.notifContent}>
                    <h4 className={styles.notifItemTitle}>{notif.title}</h4>
                    <p className={styles.notifMessage}>{notif.message}</p>
                    <div className={styles.notifFooter}>
                      <span className={styles.timeSpan}>
                        <Calendar size={12} style={{ marginRight: '4px' }} />
                        {new Date(notif.created_at).toLocaleDateString()}
                      </span>
                      {notif.type === "loan_guarantor_request" ? (
                        <button className={styles.viewMoreBtn} onClick={() => handleMarkAsRead(notif.id, true)}>
                          View Details <ChevronRight size={12} />
                        </button>
                      ) : (
                        <button className={styles.markReadBtn} onClick={() => handleMarkAsRead(notif.id)}>
                          Mark as Read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* COMPANY SELECTION MODAL */}
      {showCompanyModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Select Cooperative / Company</h3>
              <button 
                className={styles.modalCloseBtn} 
                onClick={() => setShowCompanyModal(false)}
              >
                <X size={18} />
              </button>
            </div>

            <div className={styles.modalBody}>
              <p className={styles.currentCompanyLabel}>
                You are currently on:<br /> <strong> <span style={{color: '#000'}}>{currentCompanyName}</span></strong>
              </p>

              {companiesLoading ? (
                <div className={styles.modalLoader}>
                  <Loader2 className={styles.spinner} />
                  <p>Fetching associated companies...</p>
                </div>
              ) : emailError ? (
                <div className={styles.modalError}>
                  <p>{emailError}</p>
                  <button 
                    className={styles.retryBtn} 
                    onClick={() => fetchCompanies(userEmail)}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className={styles.formGroup}>
                  <label htmlFor="company-select">Select Target Cooperative</label>
                 <select
  id="company-select"
  value={selectedCompanyId}
  onChange={(e) => setSelectedCompanyId(e.target.value)}
  className={styles.companySelect}
>
  {companies.map((workspace, index) => {
    const compId = workspace?.company?.id;
    const compName = workspace?.company?.name || "Unknown Company";
    return (
      <option key={compId || index} value={compId}>
        {compName}
      </option>
    );
  })}
</select>
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button 
                className={styles.modalCancelBtn} 
                onClick={() => setShowCompanyModal(false)}
                disabled={companiesLoading}
              >
                Cancel
              </button>
              <button 
                className={styles.modalSubmitBtn} 
                onClick={handleConfirmCompanySwitch}
                disabled={companiesLoading || companies.length === 0}
              >
                Confirm Switch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FULL SCREEN SWITCHING LOADER */}
      {isSwitching && (
        <div className={styles.globalLoaderOverlay}>
          <div className={styles.globalLoaderContent}>
            <Loader2 className={styles.globalSpinner} size={48} />
            <h2>Switching...</h2>
            <p>Setting up your new workspace overview, please wait.</p>
          </div>
        </div>
      )}
    </header>
  );
}